/* ============================================================
   FLEETTRACK — Shared product media gallery behaviour
   assets/ft-gallery.js   (paired with snippets/ft-gallery.liquid)

   ES5-flavoured to match the rest of the theme's inline section JS.
   No dependencies, no build step.

   Handles: thumbnail selection, roving-tabindex keyboard nav, mobile
   position counter, overflow "+N more" cue, and a lightbox with
   prev/next, swipe, zoom, focus trap and Escape to close.
   ============================================================ */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.__ftgReady) return;
    root.__ftgReady = true;

    var thumbsWrap = root.querySelector('[data-ftg-thumbs]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-ftg-thumb]'));
    var stage = root.querySelector('[data-ftg-stage]');
    var stageBox = root.querySelector('[data-ftg-stage-box]');
    var counter = root.querySelector('[data-ftg-counter]');
    var moreBtn = root.querySelector('[data-ftg-more]');
    var moreCount = root.querySelector('[data-ftg-more-count]');
    var lb = root.querySelector('[data-ftg-lb]');
    var lbImg = root.querySelector('[data-ftg-lb-img]');
    var lbVideo = root.querySelector('[data-ftg-lb-video]');
    var lbCount = root.querySelector('[data-ftg-lb-count]');
    var openBtn = root.querySelector('[data-ftg-open]');
    var prevBtn = root.querySelector('[data-ftg-prev]');
    var nextBtn = root.querySelector('[data-ftg-next]');
    var closeBtn = root.querySelector('[data-ftg-close]');

    if (!thumbs.length) return;

    var total = thumbs.length;
    var current = 0;
    var lastFocus = null;

    /* ── Thumbnail selection ──────────────────────────────── */
    function select(i, scrollIntoView) {
      if (i < 0) i = total - 1;
      if (i >= total) i = 0;
      current = i;

      for (var j = 0; j < total; j++) {
        var on = j === i;
        thumbs[j].setAttribute('aria-selected', on ? 'true' : 'false');
        thumbs[j].setAttribute('tabindex', on ? '0' : '-1');
      }

      var t = thumbs[i];
      if (stage && t.getAttribute('data-type') !== 'video') {
        var src = t.getAttribute('data-src');
        if (src) {
          stage.src = src;
          /* srcset would override the new src, so clear it on swap. */
          stage.removeAttribute('srcset');
          stage.removeAttribute('sizes');
        }
        var img = t.querySelector('img');
        if (img && img.alt) stage.alt = img.alt;
        /* Match the frame to this image's ratio so nothing is letterboxed. */
        var ratio = parseFloat(t.getAttribute('data-ratio'));
        if (stageBox && ratio > 0) stageBox.style.setProperty('--ftg-ratio', ratio);
      }

      if (counter) counter.textContent = (i + 1) + ' / ' + total;

      if (scrollIntoView && t.scrollIntoView) {
        t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    thumbs.forEach(function (btn, i) {
      btn.addEventListener('click', function () {
        select(i, false);
        if (btn.getAttribute('data-type') === 'video') openLightbox(i);
      });
    });

    /* Roving tabindex: arrow keys move between thumbnails. */
    if (thumbsWrap) {
      thumbsWrap.addEventListener('keydown', function (e) {
        var k = e.key;
        if (k !== 'ArrowRight' && k !== 'ArrowDown' && k !== 'ArrowLeft' &&
            k !== 'ArrowUp' && k !== 'Home' && k !== 'End') return;
        e.preventDefault();
        var next = current;
        if (k === 'ArrowRight' || k === 'ArrowDown') next = current + 1;
        else if (k === 'ArrowLeft' || k === 'ArrowUp') next = current - 1;
        else if (k === 'Home') next = 0;
        else next = total - 1;
        select(next, true);
        thumbs[current].focus();
      });
    }

    /* ── Overflow cue ─────────────────────────────────────── */
    function updateMore() {
      if (!moreBtn || !thumbsWrap) return;
      var vertical = window.matchMedia('(min-width: 641px)').matches;
      var scrollSize = vertical ? thumbsWrap.scrollHeight : thumbsWrap.scrollWidth;
      var clientSize = vertical ? thumbsWrap.clientHeight : thumbsWrap.clientWidth;
      var pos = vertical ? thumbsWrap.scrollTop : thumbsWrap.scrollLeft;
      var overflowing = scrollSize - clientSize > 4;
      var atEnd = pos + clientSize >= scrollSize - 4;

      if (!overflowing || atEnd) {
        moreBtn.hidden = true;
        return;
      }
      /* Approximate how many thumbs are still out of view. */
      var per = scrollSize / total;
      var hidden = Math.max(1, Math.round((scrollSize - clientSize - pos) / per));
      if (moreCount) moreCount.textContent = '+' + hidden + ' more';
      moreBtn.hidden = false;
    }

    if (moreBtn && thumbsWrap) {
      moreBtn.addEventListener('click', function () {
        var vertical = window.matchMedia('(min-width: 641px)').matches;
        if (vertical) thumbsWrap.scrollTop += thumbsWrap.clientHeight * 0.8;
        else thumbsWrap.scrollLeft += thumbsWrap.clientWidth * 0.8;
      });
      thumbsWrap.addEventListener('scroll', updateMore, { passive: true });
      window.addEventListener('resize', updateMore);
      /* Thumbnails are lazy-loaded, so measure again once they land. */
      if (window.requestAnimationFrame) requestAnimationFrame(updateMore);
      setTimeout(updateMore, 400);
      updateMore();
    }

    /* ── Lightbox ─────────────────────────────────────────── */
    function renderLightbox(i) {
      var t = thumbs[i];
      var isVideo = t.getAttribute('data-type') === 'video';

      if (lbVideo) {
        lbVideo.innerHTML = '';
        lbVideo.hidden = true;
      }

      if (isVideo) {
        /* Shopify-hosted media can't be embedded by id from here, so show the
           poster and send the customer to the thumbnail's own player. */
        if (lbImg) {
          lbImg.src = t.getAttribute('data-full') || t.getAttribute('data-src');
          lbImg.alt = (t.querySelector('img') || {}).alt || '';
          lbImg.hidden = false;
        }
      } else if (lbImg) {
        lbImg.hidden = false;
        lbImg.src = t.getAttribute('data-full') || t.getAttribute('data-src');
        lbImg.alt = (t.querySelector('img') || {}).alt || '';
      }

      if (lbImg) { lbImg.classList.remove('is-zoomed'); lbImg.style.transformOrigin = ''; }
      if (lbCount) lbCount.textContent = (i + 1) + ' of ' + total;
      if (prevBtn) prevBtn.hidden = total < 2;
      if (nextBtn) nextBtn.hidden = total < 2;
    }

    function openLightbox(i) {
      if (!lb) return;
      lastFocus = document.activeElement;
      select(i, true);
      renderLightbox(i);
      lb.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
      document.addEventListener('keydown', onKey);
    }

    function closeLightbox() {
      if (!lb || lb.hidden) return;
      lb.hidden = true;
      if (lbVideo) { lbVideo.innerHTML = ''; lbVideo.hidden = true; }
      if (lbImg) { lbImg.classList.remove('is-zoomed'); lbImg.style.transformOrigin = ''; }
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function step(delta) {
      var next = current + delta;
      if (next < 0) next = total - 1;
      if (next >= total) next = 0;
      select(next, true);
      renderLightbox(next);
    }

    function onKey(e) {
      if (lb.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); closeLightbox(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
      else if (e.key === 'Tab') {
        /* Simple focus trap across the lightbox controls. */
        var f = [closeBtn, prevBtn, nextBtn].filter(function (el) { return el && !el.hidden; });
        if (!f.length) return;
        var idx = f.indexOf(document.activeElement);
        e.preventDefault();
        var nx = e.shiftKey ? idx - 1 : idx + 1;
        if (nx < 0) nx = f.length - 1;
        if (nx >= f.length) nx = 0;
        f[nx].focus();
      }
    }

    if (openBtn) openBtn.addEventListener('click', function () { openLightbox(current); });
    if (closeBtn) closeBtn.addEventListener('click', closeLightbox);
    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

    if (lb) {
      lb.addEventListener('click', function (e) {
        if (e.target === lb) closeLightbox();
      });
    }

    if (lbImg) {
      /* Zoom toward the point that was clicked, then track the pointer —
         the behaviour people expect from Apple and Amazon product shots. */
      function originFrom(e) {
        var r = lbImg.getBoundingClientRect();
        var x = ((e.clientX - r.left) / r.width) * 100;
        var y = ((e.clientY - r.top) / r.height) * 100;
        lbImg.style.transformOrigin =
          Math.max(0, Math.min(100, x)) + '% ' + Math.max(0, Math.min(100, y)) + '%';
      }

      lbImg.addEventListener('click', function (e) {
        if (lbImg.classList.contains('is-zoomed')) {
          lbImg.classList.remove('is-zoomed');
          lbImg.style.transformOrigin = '';
        } else {
          originFrom(e);
          lbImg.classList.add('is-zoomed');
        }
      });

      lbImg.addEventListener('mousemove', function (e) {
        if (lbImg.classList.contains('is-zoomed')) originFrom(e);
      });
    }

    /* Swipe — on the stage and inside the lightbox. */
    function addSwipe(el, onLeft, onRight) {
      if (!el) return;
      var x0 = null, y0 = null;
      el.addEventListener('touchstart', function (e) {
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
      }, { passive: true });
      el.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        var dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) onLeft(); else onRight();
        }
        x0 = y0 = null;
      }, { passive: true });
    }

    addSwipe(root.querySelector('.ftg__stage'),
      function () { select(current + 1, true); },
      function () { select(current - 1, true); });

    addSwipe(root.querySelector('[data-ftg-lb-frame]'),
      function () { step(1); },
      function () { step(-1); });

    select(0, false);
  }

  function initAll(scope) {
    var els = (scope || document).querySelectorAll('[data-ftg]');
    Array.prototype.forEach.call(els, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Theme editor: wire up galleries in a re-rendered section. */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
